import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useScoreApi } from "../api/scoreApi";
import { prefetchDay } from "../local/prefetch";

const STORAGE_KEY = "announcer-fe.announcerContext";

const AnnouncerContext = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persist(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

// #641 — the announcer's session: org (via ActiveOrgProvider, mounted above
// this in App.jsx) + show + ring. Sticky across reloads (persisted here);
// changed only through the PIN lock (docs/score/announcer.md "Auth model").
// One announcer covers one ring, so — unlike scribe's per-class position —
// ring is the whole scope of the session.
export function AnnouncerProvider({ children }) {
  const initial = loadInitial() ?? {};
  const [selectedShow, setSelectedShowState] = useState(initial.selectedShow ?? null);
  const [selectedRing, setSelectedRingState] = useState(initial.selectedRing ?? null);
  // The PIN lock is the only way to (re)open the pickers. Deliberately NOT
  // persisted — a reload re-locks, same as scribe's pickerUnlocked.
  const [pickerUnlocked, setPickerUnlocked] = useState(false);

  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  // #912 — NEVER gate DATA READS on raw navigator.onLine. On flaky venue wifi
  // Chrome flips navigator.onLine to false and frequently never fires 'online'
  // again, which silently suppressed status badges + placings (rides kept
  // showing from cache) at a LIVE show. Always attempt fetches: every read hook
  // already falls back to its IndexedDB cache on a real failure, and the "not
  // live" banner is driven off actual data staleness + the raw browser flag
  // (browserOnline below), not off gating reads.
  const online = true;

  const [prefetchStatus, setPrefetchStatus] = useState({
    status: "idle",
    counts: null,
    error: null,
  });

  const scoreApi = useScoreApi();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => setBrowserOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    persist({ selectedShow, selectedRing });
  }, [selectedShow, selectedRing]);

  // AC3 — selecting a show (+ ring) prefetches the day into IndexedDB.
  // Re-runs whenever the session's show/ring changes (including a reload with
  // a persisted session), so the cache is warm even after the network drops.
  useEffect(() => {
    if (!selectedShow?.id || !selectedRing || !online) return undefined;
    let cancelled = false;
    setPrefetchStatus({ status: "loading", counts: null, error: null });
    prefetchDay(scoreApi, { showId: selectedShow.id, ring: selectedRing })
      .then((counts) => {
        if (!cancelled) setPrefetchStatus({ status: "done", counts, error: null });
      })
      .catch((error) => {
        if (!cancelled) setPrefetchStatus({ status: "error", counts: null, error });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedShow?.id, selectedRing, online, scoreApi]);

  const startSession = useCallback((show, ring) => {
    setSelectedShowState(show);
    setSelectedRingState(ring);
    // Picking a show/ring consumes the unlock — the pickers close behind you
    // (matches scribe's #628 behavior).
    setPickerUnlocked(false);
  }, []);

  const unlockPicker = useCallback(() => setPickerUnlocked(true), []);
  const lockPicker = useCallback(() => setPickerUnlocked(false), []);

  const value = useMemo(
    () => ({
      selectedShow,
      selectedRing,
      online,
      browserOnline,
      pickerUnlocked,
      prefetchStatus,
      isSessionReady: Boolean(selectedShow) && Boolean(selectedRing),
      startSession,
      unlockPicker,
      lockPicker,
    }),
    [
      selectedShow,
      selectedRing,
      online,
      browserOnline,
      pickerUnlocked,
      prefetchStatus,
      startSession,
      unlockPicker,
      lockPicker,
    ],
  );

  return (
    <AnnouncerContext.Provider value={value}>{children}</AnnouncerContext.Provider>
  );
}

export function useAnnouncerContext() {
  const ctx = useContext(AnnouncerContext);
  if (!ctx) {
    throw new Error("useAnnouncerContext must be used within an AnnouncerProvider");
  }
  return ctx;
}
